import { useSyncExternalStore } from "react";
import { toast } from "react-toastify";
import { getAuthScope } from "helpers/session";

let queue = [];
let failed = [];
let nextId = 0;
const dismissedDocs = new Set();
const listeners = new Set();

const emit = () => listeners.forEach((l) => l());

export const subscribeToQueue = (l) => (
  listeners.add(l), () => listeners.delete(l)
);
export const getQueueHead = () => queue[0] ?? null;
export const DOC_LABELS = {
  note: "visit note",
  discharge: "discharge document",
};

const isSameDoc = (a, b) =>
  a.requestUuid === b.requestUuid && a.docType === b.docType;

const docKey = (requestUuid, docType) => `${requestUuid}:${docType}`;

export const isJobStale = (job) => job.authScope !== getAuthScope();

export function enqueueFinalNotePdfPersist(payload, existingToastId) {
  pruneStaleJobs();
  dismissedDocs.delete(docKey(payload.requestUuid, payload.docType));

  // A newer save supersedes any job for the same request that hasn't started.
  const superseded = queue
    .slice(1)
    .filter((job) => isSameDoc(job.payload, payload));
  superseded.forEach((job) => toast.dismiss(job.toastId));
  queue = queue.filter((job) => !superseded.includes(job));

  const message = `Generating ${DOC_LABELS[payload.docType]} PDF…`;
  const toastOptions = { autoClose: false, closeOnClick: false };
  let toastId = existingToastId;
  if (toastId) {
    toast.update(toastId, {
      ...toastOptions,
      type: toast.TYPE.DEFAULT,
      render: message,
    });
  } else {
    toastId = toast(message, toastOptions);
  }
  queue = [
    ...queue,
    { id: ++nextId, toastId, payload, authScope: getAuthScope() },
  ];
  emit();
}

// Only the head can finish or fail, so late or duplicate callbacks are no-ops.
export function finishQueueHead(jobId) {
  if (queue[0]?.id !== jobId) return false;
  queue = queue.slice(1);
  emit();
  return true;
}

export function failQueueHead(jobId) {
  if (queue[0]?.id !== jobId) return false;
  failed = [...failed, queue[0]];
  queue = queue.slice(1);
  emit();
  return true;
}

export function setFailedJobToast(jobId, toastId) {
  failed = failed.map((job) => (job.id === jobId ? { ...job, toastId } : job));
}

// Closing a failure toast means "stop trying", so auto-triggers must not re-enqueue.
export function dismissFailedJob(jobId) {
  const job = takeFailedJob(jobId);
  if (job) {
    dismissedDocs.add(docKey(job.payload.requestUuid, job.payload.docType));
  }
}

export function takeFailedJob(jobId) {
  const job = failed.find((j) => j.id === jobId);
  if (!job) return null;
  failed = failed.filter((j) => j.id !== jobId);
  emit();
  return job;
}

// Drops jobs (and their toasts) that belong to a previous login.
export function pruneStaleJobs() {
  const stale = [...queue, ...failed].filter(isJobStale);
  if (!stale.length) return;
  stale.forEach((job) => toast.dismiss(job.toastId));
  queue = queue.filter((job) => !isJobStale(job));
  failed = failed.filter((job) => !isJobStale(job));
  emit();
}

export const isDocPdfQueued = (requestUuid, docType) =>
  [...queue, ...failed].some(
    (job) =>
      job.payload.requestUuid === requestUuid &&
      (!docType || job.payload.docType === docType) &&
      !isJobStale(job),
  );

export const canAutoEnqueueDocPdf = (requestUuid, docType) =>
  !isDocPdfQueued(requestUuid, docType) &&
  !dismissedDocs.has(docKey(requestUuid, docType));

export function useIsDocPdfPending(requestUuid, docType) {
  return useSyncExternalStore(subscribeToQueue, () =>
    isDocPdfQueued(requestUuid, docType),
  );
}
